import { Link } from "react-router-dom";
import PageBanner from "../components/PageBanner";
import { SITE, WHATSAPP_LINK } from "../data/siteConfig";

const SERVICES_FULL = [
  {
    icon: "bi-people-fill",
    title: "POSH Training",
    tagline: "Interactive Workplace Training",
    desc: "Interactive, scenario-based sessions for employees, managers and leadership teams. Our training goes beyond compliance — it drives real cultural change.",
    bullets: [
      "Employee awareness workshops",
      "Manager & leadership sensitization",
      "Bystander intervention training",
      "Industry-customized case studies",
      "Hindi / English delivery available",
    ],
  },
  {
    icon: "bi-shield-check",
    title: "Internal Committee Advisory",
    tagline: "IC Formation · Training · Support",
    desc: "Complete support for forming and running an effective Internal Committee — from constitution, member training to ongoing complaint handling guidance.",
    bullets: [
      "IC constitution per POSH Act, 2013",
      "IC member certification training",
      "Complaint handling playbooks",
      "Enquiry support & documentation",
      "External member provisioning",
    ],
  },
  {
    icon: "bi-file-earmark-text",
    title: "POSH Policy Drafting",
    tagline: "Legally-Vetted Custom Policies",
    desc: "Customized POSH policies drafted and reviewed by certified Company Secretaries — aligned with the POSH Act, 2013 and industry best practices.",
    bullets: [
      "Policy drafting & legal review",
      "Employee handbook integration",
      "Multi-location & remote workforce policies",
      "Annual policy refresh & updates",
      "Employee communication plan",
    ],
  },
  {
    icon: "bi-calendar-check",
    title: "Compliance & Annual Filings",
    tagline: "Zero-Stress Annual Compliance",
    desc: "End-to-end annual compliance documentation, reporting, and government filings. We handle everything so your HR team doesn't have to.",
    bullets: [
      "Annual POSH report preparation",
      "Local committee compliance (where applicable)",
      "Government / labour authority filings",
      "Compliance calendar & reminders",
      "Audit-ready documentation",
    ],
  },
  {
    icon: "bi-journal-bookmark",
    title: "Legal Advisory",
    tagline: "Corporate Governance & Advisory",
    desc: "Corporate compliance, risk mitigation, and legal support across Companies Act, M&A, due diligence, agreement vetting and ROC / MCA liaisoning.",
    bullets: [
      "Companies Act, 2013 compliance",
      "Mergers & acquisitions advisory",
      "Due diligence & agreement vetting",
      "ROC / MCA liaisoning",
      "Independent Director services",
    ],
  },
];

const Services = () => {
  return (
    <div data-testid="services-page">
      <PageBanner
        crumb="Services"
        title="Our Services"
        subtitle="End-to-end POSH compliance, training and legal advisory — tailored for Indian corporates, startups and NGOs."
        className="rs-service-banner"
      />

      <section className="section">
        <div className="container">
          {SERVICES_FULL.map((s, i) => (
            <div
              key={i}
              className={`row align-items-center g-5 ${i > 0 ? "mt-4 pt-4" : ""}`}
              style={i > 0 ? { borderTop: "1px solid var(--rs-border)" } : {}}
              data-testid={`service-detail-${i}`}
            >
              <div
                className={`col-lg-5 ${i % 2 === 1 ? "order-lg-2" : ""}`}
                data-aos={i % 2 === 0 ? "fade-right" : "fade-left"}
              >
                <div
                  style={{
                    background:
                      "linear-gradient(135deg, rgba(11,94,215,0.06), rgba(25,135,84,0.06))",
                    borderRadius: "22px",
                    padding: "56px 36px",
                    textAlign: "center",
                    border: "1px solid var(--rs-border)",
                    boxShadow: "var(--rs-soft-shadow)",
                  }}
                >
                  <div
                    style={{
                      width: "100px",
                      height: "100px",
                      borderRadius: "24px",
                      background:
                        "linear-gradient(135deg, var(--rs-primary), var(--rs-dark))",
                      margin: "0 auto 22px",
                      display: "grid",
                      placeItems: "center",
                      color: "#fff",
                      fontSize: "2.6rem",
                      boxShadow: "0 12px 28px rgba(11,94,215,0.28)",
                    }}
                  >
                    <i className={`bi ${s.icon}`}></i>
                  </div>
                  <span className="rs-expert-role">{s.tagline}</span>
                  <h3
                    style={{
                      fontSize: "1.6rem",
                      marginTop: "6px",
                      marginBottom: 0,
                    }}
                  >
                    {s.title}
                  </h3>
                </div>
              </div>

              <div
                className="col-lg-7"
                data-aos={i % 2 === 0 ? "fade-left" : "fade-right"}
              >
                <span className="eyebrow">0{i + 1} / 0{SERVICES_FULL.length}</span>
                <h2 className="section-title">{s.title}</h2>
                <div className="rs-divider"></div>
                <p className="section-lead">{s.desc}</p>

                <div className="row g-2 mt-2">
                  {s.bullets.map((b, idx) => (
                    <div className="col-md-6" key={idx}>
                      <div className="d-flex gap-2 align-items-start py-2">
                        <i
                          className="bi bi-check-circle-fill"
                          style={{
                            color: "var(--rs-accent)",
                            fontSize: "1.1rem",
                            marginTop: "3px",
                          }}
                        ></i>
                        <span style={{ color: "var(--rs-text)", fontSize: "0.96rem" }}>
                          {b}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="d-flex flex-wrap gap-3 mt-4">
                  <Link to="/contact" className="btn btn-rs-primary">
                    Enquire Now <i className="bi bi-arrow-right"></i>
                  </Link>
                  <a
                    href={WHATSAPP_LINK}
                    target="_blank"
                    rel="noreferrer"
                    className="btn btn-rs-outline"
                  >
                    <i className="bi bi-whatsapp"></i> WhatsApp
                  </a>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="section section-alt">
        <div className="container">
          <div className="rs-cta" data-aos="zoom-in">
            <div className="row align-items-center g-4">
              <div className="col-lg-8">
                <h2>Not Sure Which Service You Need?</h2>
                <p>
                  Book a free 30-minute compliance discovery call — we'll
                  tailor the right solution for your organization.
                </p>
              </div>
              <div className="col-lg-4">
                <div className="rs-cta-actions">
                  <a href={SITE.phoneHref} className="btn btn-rs-accent">
                    <i className="bi bi-telephone-fill"></i> Call {SITE.phone}
                  </a>
                  <Link to="/contact" className="btn btn-rs-ghost">
                    Contact Us <i className="bi bi-arrow-right"></i>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
